import { useMemo, useState } from "react";
import { CommandBar, type LucideIconName } from "@/deps/ui-library";

export interface CommandItem {
  id: string;
  label: string;
  icon?: LucideIconName;
  /** A short note on the right, such as a count. Also matched by the search. */
  hint?: string;
  /** Extra words the search matches that the label does not show. */
  keywords?: string;
  onSelect: () => void;
}

export interface CommandGroup {
  label: string;
  items: CommandItem[];
}

/**
 * The ⌘K search-and-jump bar, on RDS `CommandBar` (ADR-0054). RDS gives the dialog, the input and
 * the rows; the matching (label, keywords and hint), the grouping and the Up/Down/Enter handling
 * are the product's, as they were in the facade's bar this replaces. Only Accounting has one; if a
 * second product needs it, it belongs in RDS as a preset.
 */
export function SearchCommandBar({
  open,
  placeholder,
  groups,
  onClose,
}: {
  open: boolean;
  placeholder: string;
  groups: CommandGroup[];
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        items: q
          ? group.items.filter((item) =>
              `${item.label} ${item.keywords ?? ""} ${item.hint ?? ""}`.toLowerCase().includes(q),
            )
          : group.items,
      }))
      .filter((group) => group.items.length > 0);
  }, [groups, query]);

  const flat = useMemo(() => filtered.flatMap((group) => group.items), [filtered]);

  const close = () => {
    setQuery("");
    setActive(0);
    onClose();
  };
  const run = (item: CommandItem | undefined) => {
    if (!item) return;
    close();
    item.onSelect();
  };

  let index = -1;
  return (
    <CommandBar
      open={open}
      onOpenChange={(details) => {
        if (!details.open) close();
      }}
    >
      <CommandBar.Portal>
        <CommandBar.Backdrop />
        <CommandBar.Positioner>
          <CommandBar.Content>
            {/* No autoFocus: the dialog RDS builds this on moves focus to its first field on open. */}
            <CommandBar.Input
              aria-label={placeholder}
              placeholder={placeholder}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setActive((i) => Math.min(i + 1, flat.length - 1));
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setActive((i) => Math.max(i - 1, 0));
                } else if (event.key === "Enter") {
                  event.preventDefault();
                  run(flat[active]);
                }
              }}
            />
            <CommandBar.List>
              {flat.length === 0 ? (
                <CommandBar.Empty>Nothing matches “{query}”.</CommandBar.Empty>
              ) : (
                filtered.map((group) => (
                  <CommandBar.Group key={group.label} heading={group.label}>
                    {group.items.map((item) => {
                      index += 1;
                      const position = index;
                      return (
                        <CommandBar.Item
                          key={item.id}
                          icon={item.icon}
                          selected={position === active}
                          onMouseEnter={() => setActive(position)}
                          onSelect={() => run(item)}
                        >
                          <span className="flex-1">{item.label}</span>
                          {item.hint && (
                            <span className="num text-xs text-muted-foreground">{item.hint}</span>
                          )}
                        </CommandBar.Item>
                      );
                    })}
                  </CommandBar.Group>
                ))
              )}
            </CommandBar.List>
          </CommandBar.Content>
        </CommandBar.Positioner>
      </CommandBar.Portal>
    </CommandBar>
  );
}
